import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import Loading from "../components/Loading";
import Countdown from "../components/Countdown";
import FavHeart from "../components/FavHeart";
import ShareModal from "../components/ShareModal";
import useLiveLaunch from "../hooks/useLiveLaunch";
import PollWidget from "../components/PollWidget";
import CommentsSection from "../components/CommentsSection";
import { formatDateTime, statusColor } from "../lib/format";
import { ArrowLeft, MapPin, Share2, ExternalLink, Play, Radio } from "lucide-react";

function ShareModalOld() { return null; }

export default function LaunchDetailPage() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(false);
  const [flash, setFlash] = useState(false);
  const { live, updatedAt, connected } = useLiveLaunch(id, API_BASE);

  useEffect(() => {
    setLoading(true);
    api.get(`/launches/${id}`)
      .then(r => setL(r.data))
      .catch(() => setL(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Merge live SSE patches into displayed launch state
  useEffect(() => {
    if (!live || !l) return;
    setL(prev => ({ ...prev, ...live }));
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt]);

  if (loading) return <Loading message="ACCESSING MISSION DOSSIER" />;
  if (!l) return <div className="sat-label text-center py-16" data-testid="launch-not-found">MISSION NOT FOUND</div>;

  const rc = l.rocket?.configuration || {};
  const pad = l.pad || {};
  const loc = pad.location || {};
  const agency = l.launch_service_provider || {};
  const mission = l.mission || {};
  const color = statusColor(l.status?.name);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const streams = (l.vidURLs || []).slice(0, 3);

  return (
    <div className="pb-10" data-testid="page-launch-detail">
      <Link to="/launches" className="sat-label inline-flex items-center gap-2 mb-6 hover:sat-cyan" data-testid="back-launches">
        <ArrowLeft size={14} /> BACK TO MANIFEST
      </Link>

      <div className="sat-surface sat-brackets p-6 md:p-10 mb-6 relative overflow-hidden">
        {l.image && <div className="absolute inset-0 opacity-20"><img src={l.image} alt="" className="w-full h-full object-cover" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className={`border px-3 py-1 font-mono text-[10px] tracking-widest transition-all ${flash ? "sat-box-glow" : ""}`}
              style={{ color, borderColor: color }} data-testid="launch-status">
              {l.status?.name || "UNKNOWN"}
            </div>
            <span className="sat-label">MISSION ID // {String(l.id).slice(0, 8)}</span>
            {connected && (
              <span className="ml-auto flex items-center gap-2 border border-[color:var(--sat-live)] px-2 py-1" data-testid="live-connected">
                <span className="sat-live-dot" />
                <span className="font-mono text-[10px] tracking-widest text-[color:var(--sat-live)]">LIVE FEED</span>
              </span>
            )}
          </div>
          <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight mb-2" data-testid="launch-title">{l.name}</h1>
          <p className="text-[color:var(--sat-text-dim)] font-mono text-sm mb-6">{rc.full_name || rc.name}</p>

          <Countdown iso={l.net} size="lg" testid="launch-countdown" />
          <div className="sat-label mt-3">SCHEDULED · {formatDateTime(l.net)}</div>
          {l.holdreason && (
            <div className="mt-3 border border-[color:var(--sat-amber)] px-3 py-2 text-xs font-mono text-[color:var(--sat-amber)]" data-testid="launch-hold">
              HOLD · {l.holdreason}
            </div>
          )}
          {l.failreason && (
            <div className="mt-3 border border-[color:var(--sat-danger)] px-3 py-2 text-xs font-mono text-[color:var(--sat-danger)]" data-testid="launch-fail">
              FAILURE · {l.failreason}
            </div>
          )}
          {typeof l.probability === "number" && l.probability >= 0 && (
            <div className="mt-3 sat-label" data-testid="launch-probability">
              WEATHER PROBABILITY · <span className="sat-cyan">{l.probability}%</span>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <FavHeart kind="launch" ref_id={l.id} title={l.name} image={l.image} payload={{ net: l.net }} size={14} testid="launch-fav" />
            <button className="sat-btn" onClick={() => setShare(true)} data-testid="launch-share">
              <Share2 size={13} /> TRANSMIT
            </button>
            {streams[0] && (
              <a className="sat-btn sat-btn-solid" href={streams[0].url} target="_blank" rel="noopener noreferrer" data-testid="launch-stream">
                <Play size={13} /> LIVE STREAM
              </a>
            )}
          </div>
        </div>
      </div>

      {mission?.description && (
        <section className="sat-surface p-6 mb-6" data-testid="mission-briefing">
          <div className="sat-label sat-cyan mb-3">MISSION BRIEFING</div>
          <p className="text-sm text-[color:var(--sat-text)] leading-relaxed">{mission.description}</p>
          {mission.type && <div className="sat-label mt-4">TYPE · {mission.type} {mission.orbit?.name && ` · ORBIT · ${mission.orbit.name}`}</div>}
        </section>
      )}

      <div className="mb-6">
        <PollWidget launchId={l.id} variant="full" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <section className="sat-surface p-6" data-testid="vehicle-section">
          <div className="sat-label sat-cyan mb-3">VEHICLE</div>
          <h3 className="font-display font-bold text-xl">{rc.full_name || rc.name || "—"}</h3>
          <div className="mt-3 space-y-1 text-sm font-mono">
            <div className="flex justify-between border-b border-[color:var(--sat-border)] py-1"><span className="sat-label">FAMILY</span><span>{rc.family || "—"}</span></div>
            <div className="flex justify-between border-b border-[color:var(--sat-border)] py-1"><span className="sat-label">VARIANT</span><span>{rc.variant || "—"}</span></div>
            <div className="flex justify-between border-b border-[color:var(--sat-border)] py-1"><span className="sat-label">REUSABLE</span><span>{rc.reusable ? "YES" : "NO"}</span></div>
          </div>
          {rc.id && <Link to={`/fleet/${rc.id}`} className="sat-label sat-cyan mt-4 inline-flex gap-1" data-testid="link-to-rocket">→ FULL SPECS</Link>}
        </section>

        <section className="sat-surface p-6" data-testid="pad-section">
          <div className="sat-label sat-cyan mb-3">LAUNCH PAD</div>
          <h3 className="font-display font-bold text-xl">{pad.name || "TBD"}</h3>
          <div className="mt-3 flex items-start gap-2 text-sm">
            <MapPin size={14} className="mt-0.5 sat-cyan flex-shrink-0" />
            <div>
              <div>{loc.name || "—"}</div>
              <div className="sat-label mt-1">{loc.country_code || ""}</div>
            </div>
          </div>
          {pad.map_url && (
            <a href={pad.map_url} target="_blank" rel="noopener noreferrer" className="sat-label sat-cyan mt-4 inline-flex gap-1" data-testid="pad-map">
              → OPEN COORDINATES
            </a>
          )}
        </section>
      </div>

      <section className="sat-surface p-6" data-testid="operator-section">
        <div className="sat-label sat-cyan mb-3">OPERATOR</div>
        <div className="flex items-center gap-4 flex-wrap">
          {agency.logo_url && <img src={agency.logo_url} alt={agency.name} className="w-16 h-16 object-contain bg-white p-2" />}
          <div>
            <h3 className="font-display font-bold text-xl">{agency.name || "—"}</h3>
            <div className="sat-label mt-1">{agency.type || ""} · FOUNDED {agency.founding_year || "—"}</div>
          </div>
          {agency.info_url && (
            <a href={agency.info_url} target="_blank" rel="noopener noreferrer" className="sat-btn ml-auto" data-testid="agency-info">
              INFO <ExternalLink size={11} />
            </a>
          )}
        </div>
      </section>

      <ShareModal open={share} onClose={() => setShare(false)} url={shareUrl} title={l.name} testid="launch-share-modal" />

      <div className="mt-6">
        <CommentsSection targetKind="launch" targetId={l.id} />
      </div>
    </div>
  );
}
